import { inject, Injectable } from '@angular/core';
import { CanActivate, CanMatch, Route, UrlSegment, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AccessControlService } from '../services/access-control.service';
import { Permission, PERMISSIONS_BY_ROLE } from '../auth/permissions';
import { normalizeRole } from '../auth/roles';
import { combineLatest, map, take, filter } from 'rxjs';

type PolicyData = {
  roles?: string[];
  anyPerm?: Permission[];
};

@Injectable({ providedIn: 'root' })
export class PolicyGuard implements CanActivate, CanMatch {
  private readonly ac = inject(AccessControlService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot) {
    const data = (route.data ?? {}) as PolicyData;
    return this.check(data);
  }

  canMatch(route: Route, _segments: UrlSegment[]) {
    const data = (route.data ?? {}) as PolicyData;
    return this.check(data);
  }

  private check(data: PolicyData) {
    return combineLatest([this.auth.authReady$, this.auth.isAuthenticated$, this.auth.userProfile$]).pipe(
      filter(([ready]) => !!ready),
      take(1),
      map(([_, isAuth, profile]) => {
        if (!isAuth) return this.router.parseUrl('/course-catalog') as UrlTree;

        const needsPerms = !!data.anyPerm?.length;
        const needsRoles = !!data.roles?.length;

        if (!needsPerms && !needsRoles) return true;

        if (needsPerms) {
          const roleLike = profile?.role ?? null;
          if (data.anyPerm!.some((pm) => this.hasPermSync(roleLike, pm))) return true;
        }
        if (needsRoles) {
          const role = String(profile?.role ?? '');
          if (data.roles!.includes(role)) return true;
        }

        const dest = this.ac.homeFor(profile, '/');
        return this.router.parseUrl(dest ?? '/dashboard') as UrlTree;
      }),
    );
  }

  private hasPermSync(roleLike: string | null | undefined, perm: Permission): boolean {
    const nr = normalizeRole(roleLike);
    return !!nr && PERMISSIONS_BY_ROLE[nr].has(perm);
  }
}
